import { useEffect, useRef, useState } from "react";

type FrontendVersionResponse = {
	hash: string;
	lastModifiedUtc?: string;
};

type UseFrontendVersionCheckOptions = {
	endpoint?: string;
	intervalMs?: number;
	enabled?: boolean;
	onUpdate?: (response: FrontendVersionResponse) => void;
};

type UseFrontendVersionCheckResult = {
	isUpdateAvailable: boolean;
	currentHash: string | null;
	error: Error | null;
};

export const useFrontendVersionCheck = ({
	endpoint = "/frontend/version",
	intervalMs = 60_000,
	enabled = true,
	onUpdate,
}: UseFrontendVersionCheckOptions = {}): UseFrontendVersionCheckResult => {
	const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
	const [currentHash, setCurrentHash] = useState<string | null>(null);
	const [error, setError] = useState<Error | null>(null);
	const initialHashRef = useRef<string | null>(null);

	useEffect(() => {
		if (!enabled || isUpdateAvailable) {
			return;
		}

		let isDisposed = false;
		let intervalId: number | null = null;

		const loadVersion = async () => {
			try {
				const separator = endpoint.includes("?") ? "&" : "?";
				const response = await fetch(
					`${endpoint}${separator}_t=${Date.now()}`,
					{
						cache: "no-store",
					},
				);

				if (!response.ok) {
					throw new Error(
						`Version check failed with status ${response.status}`,
					);
				}

				const payload = (await response.json()) as FrontendVersionResponse;
				if (!payload?.hash) {
					throw new Error("Version check response does not include a hash");
				}

				if (isDisposed) {
					return;
				}

				setError(null);
				setCurrentHash(payload.hash);

				if (!initialHashRef.current) {
					initialHashRef.current = payload.hash;
					return;
				}

				if (initialHashRef.current !== payload.hash) {
					setIsUpdateAvailable(true);
					onUpdate?.(payload);
					if (intervalId !== null) {
						window.clearInterval(intervalId);
					}
				}
			} catch (requestError) {
				if (isDisposed) {
					return;
				}

				if (requestError instanceof Error) {
					setError(requestError);
				} else {
					setError(
						new Error("Unexpected error while checking frontend version"),
					);
				}
			}
		};

		void loadVersion();
		intervalId = window.setInterval(() => {
			void loadVersion();
		}, intervalMs);

		return () => {
			isDisposed = true;
			if (intervalId !== null) {
				window.clearInterval(intervalId);
			}
		};
	}, [enabled, endpoint, intervalMs, isUpdateAvailable, onUpdate]);

	return {
		isUpdateAvailable,
		currentHash,
		error,
	};
};
